import { EMPTY_FILTERS } from "@/lib/audienceEngine";
import { SIGNALS, RETIREMENT_STAGES } from "@/data/mosaic";

export const AUDIENCE_PRESETS = [
  { id: "all", label: "All homeowner households", description: "The complete relevant audience across every profile.", rule: "Every profile", contribution: {} },
  { id: "downsizers", label: "Likely Downsizers", description: "Profiles carrying the downsizing signal.", rule: "Downsize = Yes", contribution: { downsize: true } },
  { id: "capital", label: "Capital Release", description: "Long-established owners with potential equity to release.", rule: "Capital release = Yes", contribution: { capitalRelease: true } },
  { id: "larger", label: "Larger Homeowners", description: "Owners of four and five bedroom homes.", rule: "4 or 5 bed", contribution: { property: [4, 5] } },
  { id: "pre", label: "Pre-Retirement", description: "Established households approaching retirement.", rule: "Stage = Pre-retired", contribution: { retirement: ["PRE"] } },
  { id: "retired", label: "Retired", description: "Retired homeowner households.", rule: "Stage = Retired", contribution: { retirement: ["RET"] } },
  { id: "living", label: "Retirement Living", description: "Profiles relevant to sheltered or retirement living.", rule: "Sheltered = Yes", contribution: { sheltered: true } },
];

export const PRESET_BY_ID = Object.fromEntries(AUDIENCE_PRESETS.map((p) => [p.id, p]));

const sorted = (arr) => [...arr].sort();

export function isEmptyFilters(f) {
  return !f.retirement.length && !f.property.length && SIGNALS.every((s) => f[s.key] !== true);
}

export function presetFilters(preset) {
  return mergeContribution({ ...EMPTY_FILTERS }, preset.contribution);
}

function mergeContribution(filters, contribution) {
  const next = { ...filters };
  Object.entries(contribution).forEach(([k, v]) => {
    next[k] = Array.isArray(v) ? sorted([...new Set([...(filters[k] || []), ...v])]) : v;
  });
  return next;
}

export function isPresetActive(preset, filters) {
  if (preset.id === "all") return isEmptyFilters(filters);
  return Object.entries(preset.contribution).every(([k, v]) =>
    Array.isArray(v) ? v.every((x) => filters[k].includes(x)) : filters[k] === v
  );
}

export function togglePresetFilters(filters, preset) {
  if (preset.id === "all") return { ...EMPTY_FILTERS, opportunityMatch: filters.opportunityMatch };
  if (!isPresetActive(preset, filters)) return mergeContribution(filters, preset.contribution);
  const next = { ...filters };
  Object.entries(preset.contribution).forEach(([k, v]) => {
    next[k] = Array.isArray(v) ? filters[k].filter((x) => !v.includes(x)) : null;
  });
  return next;
}

const sameFilters = (a, b) =>
  JSON.stringify({ ...a, retirement: sorted(a.retirement), property: sorted(a.property) }) ===
  JSON.stringify({ ...b, retirement: sorted(b.retirement), property: sorted(b.property) });

export function activePresets(filters) {
  return AUDIENCE_PRESETS.filter((p) => p.id !== "all" && isPresetActive(p, filters));
}

// Returns preset ids when the filters can be expressed by presets alone, otherwise null.
export function representAsPresets(filters) {
  if (isEmptyFilters(filters)) return ["all"];
  const actives = activePresets(filters);
  const rebuilt = actives.reduce((f, p) => mergeContribution(f, p.contribution), {
    ...EMPTY_FILTERS,
    opportunityMatch: filters.opportunityMatch,
  });
  return sameFilters(rebuilt, filters) ? actives.map((p) => p.id) : null;
}

export function describeAudience(filters) {
  const ids = representAsPresets(filters);
  if (!ids) return "Custom audience";
  return ids.map((id) => PRESET_BY_ID[id].label).join(" + ");
}

// Human-readable matching rule, e.g. "Retired AND 4–5 bed AND (Downsize OR Capital release)"
export function describeLogic(filters) {
  const parts = [];
  if (filters.retirement.length && filters.retirement.length < 2) {
    parts.push(filters.retirement.map((k) => RETIREMENT_STAGES.find((s) => s.key === k).label).join(" OR "));
  }
  if (filters.property.length && filters.property.length < 4) {
    parts.push(filters.property.map((b) => `${b} bed`).join(" OR "));
  }
  const signals = SIGNALS.filter((s) => filters[s.key] === true).map((s) => s.short);
  if (signals.length) parts.push(signals.join(filters.opportunityMatch === "all" ? " AND " : " OR "));
  if (!parts.length) return [{ text: "Every homeowner profile", group: false }];
  return parts.map((p) => ({ text: p, group: p.includes(" OR ") || p.includes(" AND ") }));
}
